'use client';

interface LoadingProps {
  /** When false, renders nothing. Defaults to true for existing `<Loading />` usage. */
  isLoading?: boolean;
  /** Spinner size in px; `size < 40` uses an inline spinner (e.g. buttons), larger values use the full-screen overlay. */
  size?: number;
}

export default function Loading({ isLoading = true, size = 80 }: LoadingProps) {
  if (!isLoading) return null;

  const fullScreen = size >= 40;

  const spinner = (
    <>
      <style>{`
        @keyframes orbit {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .loading-dots {
          animation: orbit 2s linear infinite;
        }
      `}</style>
      <svg
        className="loading-dots"
        width={size}
        height={size}
        viewBox="0 0 80 80"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden
      >
        <circle cx="40" cy="15" r="6" fill="currentColor" opacity="1" />
        <circle cx="58" cy="22" r="5" fill="currentColor" opacity="0.85" />
        <circle cx="65" cy="40" r="5" fill="currentColor" opacity="0.7" />
        <circle cx="58" cy="58" r="5" fill="currentColor" opacity="0.55" />
        <circle cx="40" cy="65" r="5" fill="currentColor" opacity="0.4" />
        <circle cx="22" cy="58" r="5" fill="currentColor" opacity="0.3" />
        <circle cx="15" cy="40" r="5" fill="currentColor" opacity="0.25" />
        <circle cx="22" cy="22" r="5" fill="currentColor" opacity="0.2" />
      </svg>
    </>
  );

  if (!fullScreen) {
    return (
      <span className="inline-flex shrink-0 items-center justify-center text-current">
        {spinner}
      </span>
    );
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/8 backdrop-blur-xl backdrop-saturate-150 dark:bg-black/20"
      aria-busy="true"
      aria-live="polite"
    >
      <div className="text-primary drop-shadow-[0_2px_12px_rgba(0,0,0,0.35)] dark:text-white dark:drop-shadow-[0_2px_12px_rgba(0,0,0,0.5)]">
        {spinner}
      </div>
    </div>
  );
}
